// src/hooks/api/settings.ts

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse } from './api';
import {
  type SettingName,
  type SettingProps,
  SettingsService,
} from 'src/api/generated';
import { MINUTE_IN_MS } from 'src/utils/date';

interface SettingsResponse extends ApiResponse {
  settings: SettingProps[];
}

export const useSettings = (enabled = true): SettingsResponse => {
  const fetchSettings = async (): Promise<SettingProps[]> => {
    return await SettingsService.getSettings();
  };

  const {
    data: settings,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['settings'],
    enabled,
    queryFn: fetchSettings,
    staleTime: MINUTE_IN_MS * 2,
  });

  return {
    loading: isLoading,
    error,
    settings: settings ?? [],
    refetch,
  };
};

interface UpdateSettingParams {
  name: SettingName;
  value: string;
  scope?: 'tenant' | 'user';
}

interface UpdateSettingResponse {
  loading: boolean;
  /** Whether the last update and settings refresh completed successfully. */
  success: boolean;
  /** Retry the most recent failed update. */
  retry: () => void;
  error: Error | null;
  updateSetting: (updateSettingParams: UpdateSettingParams) => void;
  /** Clear feedback when the consumer leaves the action that produced it. */
  reset: () => void;
}

export const useUpdateSetting = (
  onUpdateSuccess?: () => void,
  onUpdateError?: () => void
): UpdateSettingResponse => {
  const queryClient = useQueryClient();

  const updateSetting = async ({
    name,
    value,
    scope,
  }: UpdateSettingParams): Promise<SettingProps> => {
    return await SettingsService.updateSetting(name, { value }, scope);
  };

  const mutation = useMutation({
    mutationFn: updateSetting,
    onSuccess: async () => {
      // Keep edits disabled until the next action can use the saved setting.
      await queryClient.invalidateQueries(
        { queryKey: ['settings'] },
        { throwOnError: true }
      );
      onUpdateSuccess?.();
    },
    onError: () => onUpdateError?.(),
  });

  return {
    loading: mutation.isPending,
    success: mutation.isSuccess,
    retry: () => {
      if (mutation.isError) {
        mutation.mutate(mutation.variables);
      }
    },
    error: mutation.error,
    updateSetting: mutation.mutate,
    reset: mutation.reset,
  };
};
